import { useEffect, useRef, useState } from "react";
import { api, FalhaApi, type GrupoWhatsappApi } from "../api";
import { useSessao } from "../estado/sessao";

// A criação do grupo é assíncrona (a Meta confirma via webhook, não na resposta do POST) — o
// painel faz polling curto enquanto o status estiver CRIANDO, até virar CRIADO ou ERRO.
const INTERVALO_POLLING_MS = 3000;

export function Dashboard() {
  const { usuario } = useSessao();
  const [grupo, setGrupo] = useState<GrupoWhatsappApi | null | undefined>(undefined);
  const [criando, setCriando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const intervaloRef = useRef<number | null>(null);

  function carregarGrupo() {
    return api
      .obterGrupo()
      .then(({ grupo }) => setGrupo(grupo))
      .catch(() => setGrupo(null));
  }

  useEffect(() => {
    void carregarGrupo();
  }, []);

  // Liga/desliga o polling conforme o status atual — só roda enquanto CRIANDO.
  useEffect(() => {
    if (grupo?.status === "CRIANDO") {
      intervaloRef.current = window.setInterval(() => void carregarGrupo(), INTERVALO_POLLING_MS);
    }
    return () => {
      if (intervaloRef.current !== null) window.clearInterval(intervaloRef.current);
    };
  }, [grupo?.status]);

  async function criarGrupo() {
    setErro(null);
    setCriando(true);
    try {
      const { grupo } = await api.criarGrupo();
      setGrupo(grupo); // vem como CRIANDO — o efeito acima liga o polling sozinho
    } catch (e) {
      setErro(e instanceof FalhaApi ? e.message : "Falha ao criar o grupo. Tente novamente.");
    } finally {
      setCriando(false);
    }
  }

  return (
    <div className="pagina">
      <h1>Painel — {usuario?.familiaNome}</h1>

      <section className="cartao">
        <h2>Grupo de WhatsApp</h2>

        {grupo === undefined && <p>Carregando…</p>}

        {(grupo === null || grupo?.status === "NAO_CRIADO") && (
          <>
            <p>
              Ainda não existe um grupo de WhatsApp para esta família. Crie um grupo e compartilhe o
              link de convite com quem for mandar ou receber lembretes.
            </p>
            {erro && (
              <div className="alerta alerta-erro" role="alert">
                {erro}
              </div>
            )}
            <button className="botao botao-primario" onClick={() => void criarGrupo()} disabled={criando}>
              {criando ? "Criando grupo…" : "Criar grupo da família"}
            </button>
          </>
        )}

        {grupo?.status === "CRIANDO" && (
          <p className="alerta alerta-aviso">
            Pedido de criação enviado — aguardando a confirmação do WhatsApp (geralmente leva só
            alguns segundos).
          </p>
        )}

        {grupo?.status === "ERRO" && (
          <>
            <div className="alerta alerta-erro" role="alert">
              Não foi possível criar o grupo{grupo.ultimoErro ? `: ${grupo.ultimoErro}` : "."} Tente
              novamente.
            </div>
            <button className="botao botao-primario" onClick={() => void criarGrupo()} disabled={criando}>
              {criando ? "Criando grupo…" : "Tentar de novo"}
            </button>
          </>
        )}

        {grupo?.status === "CRIADO" && (
          <>
            <p className="alerta alerta-sucesso">Grupo criado com sucesso.</p>
            {grupo.linkConvite && (
              <p>
                Link de convite:{" "}
                <a href={grupo.linkConvite} target="_blank" rel="noreferrer">
                  {grupo.linkConvite}
                </a>
              </p>
            )}
            <p className="dica-campo">
              Compartilhe este link com os membros da família. Qualquer um pode mandar uma mensagem
              começando com "Lembrete" no grupo para criar um lembrete automaticamente.
            </p>
          </>
        )}
      </section>
    </div>
  );
}
